/**
 * Food item string parser for the monthly report pipeline.
 *
 * Parses encoded food item strings into structured FoodItem objects and
 * provides a pretty-printer for round-trip encoding.
 *
 * CRITICAL: Structure is parsed FIRST (split by comma for items, then by
 * semicolon for fields), THEN escaped characters are decoded in the name.
 * This prevents `[U002C]` from being confused with the comma delimiter.
 *
 * Format: {escapedName};{amount};{unitName},{escapedName};{amount};{unitName},...
 * Escaped characters: [U002C] → comma, [U003B] → semicolon
 */

import type { FoodItem } from '../types';

/**
 * Decode escaped characters in a food name.
 * Replaces `[U002C]` with `,` and `[U003B]` with `;`.
 */
function unescapeName(escaped: string): string {
  return escaped.replace(/\[U002C\]/g, ',').replace(/\[U003B\]/g, ';');
}

/**
 * Encode special characters in a food name for serialization.
 * Replaces `,` with `[U002C]` and `;` with `[U003B]`.
 */
function escapeName(name: string): string {
  return name.replace(/,/g, '[U002C]').replace(/;/g, '[U003B]');
}

/**
 * Parse an encoded food item string into structured FoodItem objects.
 *
 * Steps:
 * 1. Split by comma to get individual item segments
 * 2. Split each segment by semicolon to get fields (name, amount, unit)
 * 3. Decode escaped characters in the name field
 *
 * Malformed segments (wrong number of fields, non-numeric amount) are
 * logged as warnings and skipped — the function never throws.
 *
 * @param encoded - The encoded food item string
 * @returns Array of parsed FoodItem objects
 */
export function parseFoodItems(encoded: string): FoodItem[] {
  if (!encoded || encoded.trim() === '') {
    return [];
  }

  const results: FoodItem[] = [];

  // Step 1: Split by comma to get individual item segments
  const segments = encoded.split(',');

  for (const segment of segments) {
    const trimmedSegment = segment.trim();
    if (trimmedSegment === '') {
      continue;
    }

    // Step 2: Split each segment by semicolon to get fields
    const parts = trimmedSegment.split(';');

    if (parts.length !== 3) {
      console.warn(
        `[foodParser] Malformed food segment (expected 3 fields, got ${parts.length}): "${trimmedSegment}"`
      );
      continue;
    }

    const [escapedName, amountStr, unit] = parts;

    // Validate amount is numeric
    const amount = Number(amountStr);
    if (isNaN(amount)) {
      console.warn(
        `[foodParser] Malformed food segment (non-numeric amount "${amountStr}"): "${trimmedSegment}"`
      );
      continue;
    }

    // Step 3: Decode escaped characters in the name
    const name = unescapeName(escapedName);

    results.push({
      name,
      amount,
      unit,
      rawText: trimmedSegment,
    });
  }

  return results;
}

/**
 * Pretty-print (encode) a list of FoodItem objects back into the encoded string format.
 *
 * This is the inverse of parseFoodItems — escapes commas and semicolons in names,
 * joins fields with `;`, and joins items with `,`.
 *
 * @param items - Array of FoodItem objects to encode
 * @returns The encoded food item string
 */
export function prettyPrintFoodItems(items: FoodItem[]): string {
  return items
    .map((item) => {
      const escapedName = escapeName(item.name);
      return `${escapedName};${item.amount};${item.unit}`;
    })
    .join(',');
}
